import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import { renderNarrative, hasFigToken } from "../metrics/renderNarrative";

/**
 * DecisionBlock - the board decision-required block (decision block type).
 *
 * Renders from an injected `narrative` prop (run.narrative) - not fetched, mirroring
 * ExecNarrativeBlock/NotesBlock/PageSummaryBlock. boardreport.Narrative's
 * DecisionRequired/DecisionOwner/DecisionDeadline (Task 7's narrative.go) are plain,
 * author-entered strings that ComputeNarrative always leaves "" - a human author fills them
 * in later via the narrative edit endpoint. Rendering nothing until DecisionRequired is set
 * avoids showing an empty "Decision" box on every report that hasn't been authored yet.
 *
 * SP6: decision_required/owner/deadline may contain `{{fig:KEY}}` @figure tokens, rendered live
 * via renderNarrative from the optional `metrics` prop. When ANY of them contains a token,
 * `BaseBlock`'s `isLoading` is gated on `metricsLoading` so the PDF poller waits; a token-free
 * decision keeps `isLoading={false}` (unchanged).
 */
const DecisionBlock = ({ title = "Decision", narrative, metrics, metricsLoading = false }) => {
  const decisionRequired = narrative?.decision_required;

  if (!decisionRequired) return null;

  const decisionOwner = narrative?.decision_owner;
  const decisionDeadline = narrative?.decision_deadline;

  const isLoading =
    (hasFigToken(decisionRequired) || hasFigToken(decisionOwner) || hasFigToken(decisionDeadline)) &&
    metricsLoading;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={null} className="a4-decision">
      <p className="a4-decision-required">{renderNarrative(decisionRequired, metrics)}</p>
      <dl className="a4-decision-meta">
        <dt>Owner</dt>
        <dd>{decisionOwner ? renderNarrative(decisionOwner, metrics) : "—"}</dd>
        <dt>Deadline</dt>
        <dd>{decisionDeadline ? renderNarrative(decisionDeadline, metrics) : "—"}</dd>
      </dl>
    </BaseBlock>
  );
};

DecisionBlock.propTypes = {
  title: PropTypes.string,
  narrative: PropTypes.shape({
    decision_required: PropTypes.string,
    decision_owner: PropTypes.string,
    decision_deadline: PropTypes.string,
  }),
  metrics: PropTypes.object,
  metricsLoading: PropTypes.bool,
};

export default DecisionBlock;
